/**
 * R6.3-B — canonical join for sanitized CGM geography.
 *
 * Geography and operational topology are SEPARATE concerns:
 *
 *   - `cgm_pilot_topology.json` (via `CgmTopology`) is the sole authority for
 *     sites, sectors and parcels. Every site/sector/parcel exists in the app
 *     whether or not a polygon was ever surveyed.
 *   - the sanitized KML asset contributes ONLY available geometry.
 *
 * A polygon therefore never creates, renames or re-parents an operational
 * object; it can only attach to one that the topology already declares. A name
 * that does not resolve exactly stays UNMAPPED — there is no fuzzy matching in
 * this module, by design.
 *
 * Pure functions only — no React, no network, no side effects.
 */

import type { CgmTopology } from '../../config/cgmPilot';
import {
  CGM_PARCEL_POLYGONS,
  CGM_PUMP_POINTS,
  type CgmPolygonGeometry,
} from './cgmSanitizedGeometry';

/**
 * Explicit, operator-confirmed spelling variants between the CGM survey and the
 * canonical parcel register (R6.3-B decision 2).
 *
 * This table is a closed list for one known dataset — NOT a matching strategy.
 * Keys are the verbatim KML strings, including their exact accents:
 * `Barthés` (U+00E9) on the left resolves to `Barthès` (U+00E8) on the right.
 * Anything absent from this table must match a canonical name exactly or stay
 * UNMAPPED.
 */
export const KML_PARCEL_ALIASES: Readonly<Record<string, string>> = {
  'Marselan Barthés': 'Marselan Barthès',
  'Merlot Puy vieux': 'Merlot vieux Puy',
  'Marlot Puy Jeune': 'Merlot jeune Puy',
  'Cabernet Gortti': 'Cabernet Grotti',
};

/**
 * Pumping placemarks whose KML name explicitly names a canonical site
 * (R6.3-B decision 1). GEOGRAPHY ONLY.
 *
 * This never asserts a relay, device, channel or commissioning fact. The KML's
 * relay identities for Escales/Luc contradict
 * `CGM_FIELD_COMMISSIONING_BINDINGS_R2.json`, which remains authoritative for
 * every operational binding; the discrepancy is deferred to field verification
 * and is deliberately not resolved in software.
 */
const PUMP_SITE_BY_KML_NAME: Readonly<Record<string, string>> = {
  'Pompage Roubia': 'Roubia',
  'Pompage Moulin Luc': 'Luc',
  'Pompage surpression BRL Escales': 'Escales',
};

/** A polygon successfully attached to a canonical sector. */
export interface MappedParcelGeometry {
  /** Verbatim KML name the geometry came from. */
  kmlName: string;
  /** Canonical parcel name from the topology. */
  canonicalParcel: string;
  siteName: string;
  sectorCode: string;
  /** Canonical sector view id, identical to `SoverdiSectorView.id`. */
  sectorId: string;
  ring: ReadonlyArray<readonly [number, number]>;
}

/** A surveyed pumping coordinate attached to a canonical site. Geography only. */
export interface MappedPumpPoint {
  kmlName: string;
  siteName: string;
  position: readonly [number, number];
}

export interface CgmGeography {
  parcels: MappedParcelGeometry[];
  pumps: MappedPumpPoint[];
  /** KML names that could not be resolved canonically. Never rendered as sectors. */
  unmapped: string[];
}

interface ParcelLocation {
  siteName: string;
  sectorCode: string;
}

/** Only the site tree is needed to place geometry — the rest of the topology is irrelevant here. */
export type CgmTopologySites = Pick<CgmTopology, 'sites'>;

/**
 * Index canonical parcel name -> location. A name occurring in more than one
 * sector is recorded as ambiguous and removed: the topology legitimately reuses
 * some parcel names across sites (e.g. `Vermentino Franc` in two Luc sectors),
 * and geometry must never guess which one it meant.
 */
function indexParcels(topology: CgmTopologySites): Map<string, ParcelLocation | null> {
  const index = new Map<string, ParcelLocation | null>();
  for (const site of topology.sites) {
    for (const sector of site.sectors) {
      for (const parcel of sector.parcels) {
        if (index.has(parcel.name)) {
          index.set(parcel.name, null); // ambiguous — fail closed
          continue;
        }
        index.set(parcel.name, { siteName: site.name, sectorCode: sector.code });
      }
    }
  }
  return index;
}

/**
 * Resolve a KML placemark name to a canonical parcel name.
 *
 * Exactly two ways to succeed: an explicit confirmed alias, or an exact match
 * against a canonical name. Returns null otherwise — approximate spellings do
 * NOT resolve.
 */
export function resolveCanonicalParcelName(
  kmlName: string,
  canonicalNames: ReadonlySet<string>,
): string | null {
  const alias = KML_PARCEL_ALIASES[kmlName];
  if (alias !== undefined) {
    // An alias must still name a parcel the topology actually declares.
    return canonicalNames.has(alias) ? alias : null;
  }
  return canonicalNames.has(kmlName) ? kmlName : null;
}

/** Canonical sector view id — the shared key between geometry and sector views. */
export function sectorViewId(siteName: string, sectorCode: string): string {
  return `${siteName}:${sectorCode}`;
}

/** Attach the sanitized geometry to the canonical topology. */
export function buildCgmGeography(
  topology: CgmTopologySites,
  polygons: ReadonlyArray<CgmPolygonGeometry> = CGM_PARCEL_POLYGONS,
): CgmGeography {
  const parcelIndex = indexParcels(topology);
  const canonicalNames = new Set(parcelIndex.keys());
  const siteNames = new Set(topology.sites.map((site) => site.name));

  const parcels: MappedParcelGeometry[] = [];
  const unmapped: string[] = [];

  for (const polygon of polygons) {
    const canonicalParcel = resolveCanonicalParcelName(polygon.kmlName, canonicalNames);
    const location = canonicalParcel ? parcelIndex.get(canonicalParcel) : undefined;
    if (!canonicalParcel || !location) {
      unmapped.push(polygon.kmlName);
      continue;
    }
    parcels.push({
      kmlName: polygon.kmlName,
      canonicalParcel,
      siteName: location.siteName,
      sectorCode: location.sectorCode,
      sectorId: sectorViewId(location.siteName, location.sectorCode),
      ring: polygon.ring,
    });
  }

  const pumps: MappedPumpPoint[] = [];
  for (const point of CGM_PUMP_POINTS) {
    const siteName = PUMP_SITE_BY_KML_NAME[point.kmlName];
    if (!siteName || !siteNames.has(siteName)) {
      unmapped.push(point.kmlName);
      continue;
    }
    pumps.push({ kmlName: point.kmlName, siteName, position: point.position });
  }

  return { parcels, pumps, unmapped };
}

/** Mapped polygons of one site. Empty is a truthful answer, not a failure. */
export function parcelsForSite(
  geography: CgmGeography,
  siteName: string,
): MappedParcelGeometry[] {
  return geography.parcels.filter((parcel) => parcel.siteName === siteName);
}

/**
 * Every mapped polygon of one sector. A sector with several surveyed parcels
 * highlights all of them; the original rings are preserved, never merged.
 */
export function parcelsForSector(
  geography: CgmGeography,
  sectorId: string,
): MappedParcelGeometry[] {
  return geography.parcels.filter((parcel) => parcel.sectorId === sectorId);
}

/** Surveyed pumping coordinates of one site. Lezignan legitimately has none. */
export function pumpsForSite(geography: CgmGeography, siteName: string): MappedPumpPoint[] {
  return geography.pumps.filter((pump) => pump.siteName === siteName);
}

export type LatLngBounds = readonly [readonly [number, number], readonly [number, number]];

/**
 * Bounds of real coordinates, or null when there are none.
 *
 * Null means "nothing surveyed" and must stay null — callers render the
 * truthful empty-geography state rather than centring on an invented point.
 */
export function boundsOf(
  rings: ReadonlyArray<ReadonlyArray<readonly [number, number]>>,
  points: ReadonlyArray<readonly [number, number]> = [],
): LatLngBounds | null {
  const all: Array<readonly [number, number]> = [...points];
  for (const ring of rings) all.push(...ring);
  if (all.length === 0) return null;

  let minLat = all[0][0];
  let maxLat = all[0][0];
  let minLng = all[0][1];
  let maxLng = all[0][1];
  for (const [lat, lng] of all) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}
